// Starting the console, and getting back on its feet after an update.
//
// An update replaces the built files, and the old ones are gone: a tab left
// open asks for a page's script by its old name and gets a 404, and so does
// a browser that kept the old index.html. Reloading fetches the new
// index.html and the new names. Once a minute at most, so a file missing
// for some other reason cannot send the page round in a loop.
import type { Router } from 'vue-router'

const RELOADED_AT = 'cdnx_reloaded_at'
const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i

function isChunkError(err: unknown) {
  return CHUNK_ERROR.test(String((err as Error | undefined)?.message ?? err))
}

// reloadOnce reloads (at to, when given) unless it already did within the
// minute; false when it did not.
function reloadOnce(to?: string) {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT) || 0)
    if (Date.now() - last < 60_000) return false
    sessionStorage.setItem(RELOADED_AT, String(Date.now()))
  } catch {
    return false
  }
  if (to) window.location.assign(to)
  else window.location.reload()
  return true
}

// recoverStaleChunks reloads when a page's script, or what it preloads, is
// gone with an update — at the page that was being opened, which Vite's
// preload error does not name, hence the navigation kept here.
export function recoverStaleChunks(router: Router) {
  let opening = ''
  router.beforeEach((to) => {
    opening = to.fullPath
  })
  router.afterEach(() => {
    opening = ''
  })
  window.addEventListener('vite:preloadError', (e) => {
    if (reloadOnce(opening || undefined)) e.preventDefault()
  })
  router.onError((err, to) => {
    if (isChunkError(err)) reloadOnce(to.fullPath)
  })
}

// bootFailed is the first navigation failing: a script gone with an update
// reloads; anything else — the account not answering, a script that would
// not load — leaves index.html's boot screen saying so, with a reload.
export function bootFailed(err: unknown) {
  console.error(err)
  if (isChunkError(err) && reloadOnce()) return
  ;(window as { __cdnxBootStalled?: (kind: string) => void }).__cdnxBootStalled?.('failed')
}
